/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useFileUpload.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for handling file uploads in branding form.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { useAuthStore } from '../../../store/auth';
import { uploadBrandingFile } from '../api/brandingApi';
import { API_CONFIG } from '../../../config/api';
import type { BrandingImageField, BrandingPreviews } from '../types';

export function useFileUpload(
  onFileUploaded: (field: BrandingImageField, path: string) => void
) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || 'main';

  const [uploadingField, setUploadingField] =
    useState<BrandingImageField | null>(null);
  const [previews, setPreviews] = useState<BrandingPreviews>(
    {} as BrandingPreviews
  );

  const resolveAssetPath = useCallback((pathValue: string | null | undefined) => {
    if (!pathValue) return null;
    if (
      pathValue.startsWith('http://') ||
      pathValue.startsWith('https://') ||
      pathValue.startsWith('data:')
    ) {
      return pathValue;
    }
    const apiBaseUrl = API_CONFIG.BASE_URL || window.location.origin;
    return `${apiBaseUrl}${pathValue}`;
  }, []);

  const handleFileUpload = useCallback(
    async (field: BrandingImageField, file: File) => {
      setUploadingField(field);
      try {
        // Create preview from file
        const reader = new FileReader();
        reader.onload = (event) => {
          setPreviews((prev) => ({
            ...prev,
            [field]: event.target?.result as string,
          }));
        };
        reader.readAsDataURL(file);

        // Upload to server
        const result = await uploadBrandingFile(file, tenantSlug, user?.id ? Number(user.id) : undefined);

        // Update form data with server path
        onFileUploaded(field, result.path);

        // Update preview to use resolved URL from server
        setPreviews((prev) => ({
          ...prev,
          [field]: resolveAssetPath(result.path),
        }));
      } catch (error) {
        showError(t('branding.errors.uploadFailed'));
        // Reset preview on error
        setPreviews((prev) => ({
          ...prev,
          [field]: null,
        }));
      } finally {
        setUploadingField(null);
      }
    },
    [tenantSlug, user?.id, onFileUploaded, resolveAssetPath, showError, t]
  );

  const handleRemoveFile = useCallback((field: BrandingImageField) => {
    setPreviews((prev) => ({
      ...prev,
      [field]: null,
    }));
  }, []);

  const setPreview = useCallback(
    (field: BrandingImageField, value: string | null) => {
      setPreviews((prev) => ({
        ...prev,
        [field]: value ? resolveAssetPath(value) : null,
      }));
    },
    [resolveAssetPath]
  );

  return {
    uploadingField,
    previews,
    handleFileUpload,
    handleRemoveFile,
    setPreview,
  };
}

